import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { companionsApi } from "@/lib/api";

export interface Companion {
  id: string;
  mention: string;
  kind: string;
  resolved: boolean;
  traveler_id?: string | null;
  display_name?: string | null;
  relationship?: string | null;
}

interface CompanionChipsProps {
  companions: Companion[];
  onAdd: (companion: Companion) => void;
}

/** Pure presentational chip row — no data fetching. Exported for unit tests. */
export function CompanionChips({ companions, onAdd }: CompanionChipsProps) {
  if (companions.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground font-medium">Who&apos;s coming:</span>
      {companions.map((c) =>
        c.traveler_id ? (
          <Link
            key={c.id}
            to={`/travelers/${c.traveler_id}`}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs font-medium hover:bg-muted transition-colors"
          >
            {c.display_name ?? c.mention}
          </Link>
        ) : (
          <button
            key={c.id}
            onClick={() => onAdd(c)}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border bg-background px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-muted transition-colors"
          >
            {c.mention}
            <span className="text-muted-foreground/60" aria-hidden="true">· add</span>
            <UserPlus className="h-3 w-3" aria-hidden="true" />
          </button>
        ),
      )}
    </div>
  );
}

interface CompanionStripProps {
  tripId: string;
  description?: string | null;
}

/** Data-fetching wrapper — auto-resolves once when description contains "with" and list is empty. */
export default function CompanionStrip({ tripId, description }: CompanionStripProps) {
  const queryClient = useQueryClient();
  const resolvedRef = useRef(false);

  const { data } = useQuery({
    queryKey: ["companions", tripId],
    queryFn: () => companionsApi.list(tripId),
    enabled: !!tripId,
  });

  const companions: Companion[] = data?.companions ?? [];

  // Auto-resolve once: list is empty + description has "with" + haven't resolved this mount.
  useEffect(() => {
    if (
      data !== undefined &&
      companions.length === 0 &&
      /\bwith\b/i.test(description ?? "") &&
      !resolvedRef.current
    ) {
      resolvedRef.current = true;
      companionsApi.resolve(tripId).then(() => {
        queryClient.invalidateQueries({ queryKey: ["companions", tripId] });
        // Resolving companions bumps the trip's party size — refresh the trip so
        // the plan's traveler count reflects it without a manual reload.
        queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
      });
    }
  }, [data, description, tripId, queryClient]);

  const handleAdd = (companion: Companion) => {
    // A NAME mention ("Ella") creates a traveler with that name. A RELATIONSHIP
    // mention ("my wife") must NOT create a traveler literally named "my wife" —
    // ask for the person's actual name and set the relationship instead.
    let create_name = companion.mention;
    let relationship: string | undefined;
    if (companion.kind === "relationship") {
      const rel = companion.mention.replace(/^(?:my|our|the)\s+/i, "").trim();
      const name = window.prompt(`What's your ${rel}'s name?`)?.trim();
      if (!name) return; // cancelled — leave the chip unresolved
      create_name = name;
      relationship = rel;
    }
    companionsApi
      .link(tripId, companion.id, { create_name, relationship })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["companions", tripId] });
      });
  };

  return (
    <CompanionChips companions={companions} onAdd={handleAdd} />
  );
}
